import React, { useState } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  AppUserGroupDto,
  VehicleDto,
  BatteryDto,
  BatterySubscriptionDto,
  HomeChargerSubscriptionDto,
} from "../../generated/api-client";
import { VehicleEditForm } from "./VehicleEditForm";
import { BatteryEditForm } from "./BatteryEditForm";
import { formatDate } from "../../utils/date.utils";
import { useI18n } from "../../i18n-context";

interface GroupsSectionProps {
  groups: AppUserGroupDto[];
  onVehicleUpdate?: (
    groupIndex: number,
    vehicleIndex: number,
    updatedVehicle: VehicleDto
  ) => void;
  onBatteryUpdate?: (
    groupIndex: number,
    batteryIndex: number,
    updatedBattery: BatteryDto
  ) => void;
}

export function GroupsSection({
  groups,
  onVehicleUpdate,
  onBatteryUpdate,
}: GroupsSectionProps) {
  const intl = useIntl();
  const { locale } = useI18n();
  const [editingVehicle, setEditingVehicle] = useState<VehicleDto | null>(null);
  const [editingBattery, setEditingBattery] = useState<BatteryDto | null>(null);
  const [copiedGroupId, setCopiedGroupId] = useState<string | null>(null);

  const handleCopyGroupId = (e: React.MouseEvent, groupId: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(groupId).then(() => {
      setCopiedGroupId(groupId);
      setTimeout(() => setCopiedGroupId(null), 1500);
    });
  };

  const handleVehicleClick = (vehicle: VehicleDto) => {
    if (editingVehicle?.id === vehicle.id) {
      setEditingVehicle(null);
      return;
    }
    setEditingVehicle(vehicle);
    setEditingBattery(null);
  };

  const handleBatteryClick = (battery: BatteryDto) => {
    if (editingBattery?.id === battery.id) {
      setEditingBattery(null);
      return;
    }
    setEditingBattery(battery);
    setEditingVehicle(null);
  };

  const handleVehicleSaveSuccess = (
    groupIndex: number,
    vehicleIndex: number,
    updatedVehicle: VehicleDto
  ) => {
    setEditingVehicle(null);
    onVehicleUpdate?.(groupIndex, vehicleIndex, updatedVehicle);
  };

  const handleBatterySaveSuccess = (
    groupIndex: number,
    batteryIndex: number,
    updatedBattery: BatteryDto
  ) => {
    setEditingBattery(null);
    onBatteryUpdate?.(groupIndex, batteryIndex, updatedBattery);
  };

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {intl.formatMessage({ id: "noGroupsFound" })}
      </Typography>
    );
  }

  return (
    <>
      {groups.map((group: AppUserGroupDto, groupIndex: number) => (
        <Accordion key={group.id} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {group.name ||
                `${intl.formatMessage({ id: "group" })} ${group.id}`}
            </Typography>
            <Tooltip
              title={
                copiedGroupId === group.id
                  ? intl.formatMessage({ id: "copied" })
                  : intl.formatMessage({ id: "clickToCopy" })
              }
              arrow
              placement="top"
            >
              <Chip
                icon={<ContentCopyIcon sx={{ fontSize: 18 }} />}
                label={group.id}
                size="medium"
                variant="outlined"
                onClick={(e) => handleCopyGroupId(e, group.id)}
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 500,
                  "& .MuiChip-icon": { color: "inherit" },
                  mr: 3,
                  p: 1,
                }}
              />
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Vehicles Section */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {intl.formatMessage({ id: "vehicles" })} (
                  {group.vehicles?.length || 0})
                </Typography>
                {group.vehicles && group.vehicles.length > 0 ? (
                  <List>
                    {group.vehicles.map(
                      (vehicle: VehicleDto, vehicleIndex: number) => (
                        <React.Fragment key={vehicle.id}>
                          <ListItemButton
                            onClick={() => handleVehicleClick(vehicle)}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            <ListItemText
                              primary={
                                vehicle.name ||
                                `${intl.formatMessage({ id: "vehicle" })} ${vehicle.serialNumber}`
                              }
                              secondary={`${intl.formatMessage({ id: "serial" })}: ${vehicle.serialNumber || intl.formatMessage({ id: "nA" })}`}
                            />
                            <EditIcon />
                          </ListItemButton>
                          {editingVehicle?.id === vehicle.id && (
                            <VehicleEditForm
                              vehicle={vehicle}
                              onSaveSuccess={(updatedVehicle) =>
                                handleVehicleSaveSuccess(
                                  groupIndex,
                                  vehicleIndex,
                                  updatedVehicle
                                )
                              }
                              onCancel={() => setEditingVehicle(null)}
                            />
                          )}
                        </React.Fragment>
                      )
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "noVehiclesInGroup" })}
                  </Typography>
                )}
              </Box>

              {/* Batteries Section */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {intl.formatMessage({ id: "batteries" })} (
                  {group.batteries?.length || 0})
                </Typography>
                {group.batteries && group.batteries.length > 0 ? (
                  <List>
                    {group.batteries.map(
                      (battery: BatteryDto, batteryIndex: number) => (
                        <React.Fragment key={battery.id}>
                          <ListItemButton
                            onClick={() => handleBatteryClick(battery)}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              mb: 1,
                            }}
                          >
                            <ListItemText
                              primary={`${intl.formatMessage({ id: "battery" })} ${battery.serialNumber || battery.id}`}
                              secondary={`${intl.formatMessage({ id: "soc" })}: ${battery.stateOfCharge}%`}
                            />
                            <EditIcon />
                          </ListItemButton>
                          {editingBattery?.id === battery.id && (
                            <BatteryEditForm
                              battery={battery}
                              onSaveSuccess={(updatedBattery) =>
                                handleBatterySaveSuccess(
                                  groupIndex,
                                  batteryIndex,
                                  updatedBattery
                                )
                              }
                              onCancel={() => setEditingBattery(null)}
                            />
                          )}
                        </React.Fragment>
                      )
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: "noBatteriesInGroup" })}
                  </Typography>
                )}
              </Box>

              {/* Battery Subscriptions Section */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {intl.formatMessage({ id: "batterySubscriptions" })} (
                  {group.batterySubscriptions?.length || 0})
                </Typography>
                {group.batterySubscriptions &&
                group.batterySubscriptions.length > 0 ? (
                  <List>
                    {group.batterySubscriptions.map(
                      (sub: BatterySubscriptionDto, subIndex: number) => (
                        <ListItem
                          key={sub.shopSubscriptionId ?? subIndex}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <ListItemText
                            primary={
                              sub.shopSubscriptionId ||
                              `${intl.formatMessage({ id: "batterySubscription" })} ${subIndex + 1}`
                            }
                            secondary={`${intl.formatMessage({ id: "status" })}: ${sub.status} · ${intl.formatMessage({ id: "expirationDate" })}: ${formatDate(sub.expirationDate, locale)}`}
                          />
                        </ListItem>
                      )
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({
                      id: "noBatterySubscriptionsInGroup",
                    })}
                  </Typography>
                )}
              </Box>

              {/* Home Charger Subscriptions Section */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {intl.formatMessage({ id: "homeChargerSubscriptions" })} (
                  {group.homeChargerSubscriptions?.length || 0})
                </Typography>
                {group.homeChargerSubscriptions &&
                group.homeChargerSubscriptions.length > 0 ? (
                  <List>
                    {group.homeChargerSubscriptions.map(
                      (sub: HomeChargerSubscriptionDto, subIndex: number) => (
                        <ListItem
                          key={sub.shopSubscriptionId ?? subIndex}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <ListItemText
                            primary={
                              sub.shopSubscriptionId ||
                              `${intl.formatMessage({ id: "homeChargerSubscription" })} ${subIndex + 1}`
                            }
                            secondary={`${intl.formatMessage({ id: "status" })}: ${sub.status} · ${intl.formatMessage({ id: "expirationDate" })}: ${formatDate(sub.expirationDate, locale)}`}
                          />
                        </ListItem>
                      )
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({
                      id: "noHomeChargerSubscriptionsInGroup",
                    })}
                  </Typography>
                )}
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
}
